import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useClient } from "@/hooks/useClients";
import { useClientsPath } from "@/hooks/useClientsPath";
import { useDeployments, useCreateDeployment, useKnowledgeBases } from "@/hooks/useDeployments";
import { useConversationScripts } from "@/hooks/useConversationScripts";
import {
  DEPLOYMENT_EFFORTS,
  DEPLOYMENT_ENGINES,
  ENGINE_LABELS,
  EFFORT_LABELS,
  EFFORT_SHORT_LABELS,
  MODEL_CATALOG,
  SELECTABLE_MODEL_IDS,
  defaultEffortFor,
  defaultMaxTokensFor,
  modelLabel,
  modelUsesThinking,
  type DeploymentEngine,
  type ModelEffort,
  type SelectableModelId,
} from "@school-sales-ai/shared";

/**
 * How a deployment version actually runs, as "4096 tok (default) · esfuerzo bajo".
 *
 * Shows the EFFECTIVE value, not only what the row stored — a version that
 * overrode nothing still runs at something, and "no dice nada" is not an answer to
 * "¿con qué está corriendo?". Values the row did not store are marked `(default)`.
 *
 * That marker is load-bearing, and the table carries a footnote saying so: a
 * default is resolved from the catalog as it is TODAY, while the row was created
 * against whatever the catalog held then. For an archived version the marked value
 * is "what this config would resolve to now", not a record of what it ran. The
 * stored overrides — the unmarked parts — are the only real history here.
 *
 * With no model stored there is nothing to resolve against: the effective model is
 * the backend's `ANTHROPIC_MODEL`, which the CRM does not know. Those rows show the
 * model cell alone rather than guessing.
 *
 * `effort` appears only where it does something. On a model that omits `thinking`
 * the value is inert, and showing it would suggest a knob that was turned.
 */
function runtimeDetails(d: {
  model: string | null;
  maxTokens: number | null;
  effort: string | null;
  engine: string | null;
}): string {
  // The engine is not a model setting, so it is reported even for a version that
  // named no model — "v1" is never printed because it is the default everywhere.
  const engine = d.engine === "v2" ? ["motor v2"] : [];
  if (!d.model) return engine.join(" · ");

  const parts: string[] = [...engine];

  const tokens = d.maxTokens ?? defaultMaxTokensFor(d.model);
  if (tokens) parts.push(`${tokens} tok${d.maxTokens ? "" : " (default)"}`);

  if (modelUsesThinking(d.model)) {
    const effort = d.effort ?? defaultEffortFor(d.model);
    if (effort) {
      const label =
        EFFORT_SHORT_LABELS[effort as ModelEffort] ?? `esfuerzo ${effort}`;
      parts.push(`${label}${d.effort ? "" : " (default)"}`);
    }
  }

  return parts.join(" · ");
}

export function DeploymentEditor(): React.JSX.Element {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const clientsPath = useClientsPath();

  const { data: client, isPending: clientPending } = useClient(clientId);
  const { data: deployments, isPending: deploymentsPending } = useDeployments(clientId);
  const { data: allKbs, isPending: kbsPending } = useKnowledgeBases(clientId);
  const { data: scripts, isPending: scriptsPending } = useConversationScripts();
  const createMutation = useCreateDeployment(clientId!);

  const active = deployments?.find((d) => d.status === "active");

  const [agentName, setAgentName] = useState("");
  const [agentTone, setAgentTone] = useState("");
  const [agentRole, setAgentRole] = useState("");
  const [knowledgeBaseId, setKnowledgeBaseId] = useState("");
  const [scriptId, setScriptId] = useState("");
  // "" = use the system default. Not the same as a model id, and not the same as
  // null-the-database-value — the create payload omits the key entirely.
  const [model, setModel] = useState("");
  const [maxTokens, setMaxTokens] = useState("");
  const [effort, setEffort] = useState("");
  const [engine, setEngine] = useState("");

  useEffect(() => {
    if (active) {
      setAgentName(active.agentName);
      setAgentTone(active.agentTone ?? "");
      setAgentRole(active.agentRole ?? "");
      setKnowledgeBaseId(active.knowledgeBaseId);
      setScriptId(active.scriptId);
      setModel(active.model ?? "");
      setMaxTokens(active.maxTokens?.toString() ?? "");
      setEffort(active.effort ?? "");
      setEngine(active.engine ?? "");
    }
    // Only re-initialize when the active deployment changes by id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);

  const kbOptions = allKbs ?? [];

  // `effort` only does something on a model that runs thinking. Rather than store a
  // value nothing reads, the control appears only when the chosen model uses it.
  const showEffort = model ? modelUsesThinking(model) : false;

  const isPending = clientPending || deploymentsPending || kbsPending || scriptsPending;

  if (isPending) return <div className="p-6 text-sm text-ink-500">Cargando…</div>;

  const canSave =
    agentName.trim() &&
    knowledgeBaseId &&
    scriptId &&
    !createMutation.isPending;

  function handleSave(): void {
    createMutation.mutate(
      {
        // Explicit null, never undefined: this form displays each of these, so a
        // blank box is the user saying "no override", not "leave it as it was".
        // Sending undefined made the backend inherit the previous version instead,
        // which is how a deployment created to get the model's 4096 default came
        // back with the 2048 its predecessor happened to carry.
        agentName: agentName.trim(),
        agentTone: agentTone.trim() || null,
        agentRole: agentRole.trim() || null,
        model: (model || null) as SelectableModelId | null,
        maxTokens: maxTokens.trim() ? Number(maxTokens) : null,
        // Null for a model that does not reason — storing a value nothing reads is
        // how a setting acquires a reputation for being broken.
        effort: showEffort && effort ? (effort as ModelEffort) : null,
        engine: (engine || null) as DeploymentEngine | null,
        knowledgeBaseId,
        scriptId,
      },
      {
        onSuccess: () => {
          toast.success("Deployment creado. El deployment anterior fue archivado.");
        },
        onError: (err) => {
          toast.error(err.message ?? "Error al crear deployment.");
        },
      },
    );
  }

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => void navigate(clientsPath)}
          className="text-sm text-ink-500 hover:text-ink-900"
        >
          ← Clientes
        </button>
        <span className="text-ink-200">/</span>
        <h1 className="text-xl font-semibold text-ink-900">
          {client?.name ?? clientId} — Deployment
        </h1>
      </div>

      {active && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          Deployment activo: v{active.version} · {active.agentName} · Script "
          {active.script.name}" v{active.script.version} ·{" "}
          {active.model ? modelLabel(active.model) : "modelo por default"}
          {runtimeDetails(active) && ` · ${runtimeDetails(active)}`}
        </div>
      )}

      <div className="max-w-lg rounded-lg border border-ink-200 bg-white p-6">
        <h2 className="mb-5 text-sm font-semibold text-ink-700">Nuevo deployment</h2>
        <div className="flex flex-col gap-4">
          <Field label="Nombre del agente">
            <input
              type="text"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              placeholder="Vera"
              className="w-full rounded border border-ink-200 px-3 py-2 text-sm text-ink-800 focus:border-moss focus:outline-none"
            />
          </Field>

          <Field label="Rol (opcional)">
            <input
              type="text"
              value={agentRole}
              onChange={(e) => setAgentRole(e.target.value)}
              maxLength={120}
              placeholder="asistente de admisiones"
              className="w-full rounded border border-ink-200 px-3 py-2 text-sm text-ink-800 focus:border-moss focus:outline-none"
            />
            <p className="mt-1 text-xs text-ink-400">
              Sustituye el rol del script. Se usa en el system prompt y en el encabezado
              del widget. Vacío = el del script.
            </p>
          </Field>

          <Field label="Tono (opcional)">
            <input
              type="text"
              value={agentTone}
              onChange={(e) => setAgentTone(e.target.value)}
              placeholder="cálida, profesional y orientada a resultados"
              className="w-full rounded border border-ink-200 px-3 py-2 text-sm text-ink-800 focus:border-moss focus:outline-none"
            />
          </Field>

          <Field label="Base de conocimiento">
            {kbOptions.length === 0 ? (
              <input
                type="text"
                value={knowledgeBaseId}
                onChange={(e) => setKnowledgeBaseId(e.target.value)}
                placeholder="UUID de la KB"
                className="w-full rounded border border-ink-200 px-3 py-2 font-mono text-sm text-ink-800 focus:border-moss focus:outline-none"
              />
            ) : (
              <select
                value={knowledgeBaseId}
                onChange={(e) => setKnowledgeBaseId(e.target.value)}
                className="w-full rounded border border-ink-200 px-3 py-2 text-sm text-ink-800 focus:border-moss focus:outline-none"
              >
                <option value="">— Selecciona —</option>
                {kbOptions.map((kb) => (
                  <option key={kb.id} value={kb.id}>
                    KB v{kb.version}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Script de conversación">
            <select
              value={scriptId}
              onChange={(e) => setScriptId(e.target.value)}
              className="w-full rounded border border-ink-200 px-3 py-2 text-sm text-ink-800 focus:border-moss focus:outline-none"
            >
              <option value="">— Selecciona —</option>
              {scripts
                ?.filter((s) => s.status === "active")
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} v{s.version}
                  </option>
                ))}
            </select>
          </Field>

          <Field label="Modelo (opcional)">
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full rounded border border-ink-200 px-3 py-2 text-sm text-ink-800 focus:border-moss focus:outline-none"
            >
              <option value="">— Usar el default del sistema —</option>
              {SELECTABLE_MODEL_IDS.map((id) => {
                const { inputPerMtok, outputPerMtok } = MODEL_CATALOG[id].pricing;
                return (
                  <option key={id} value={id}>
                    {modelLabel(id)} · ${inputPerMtok}/${outputPerMtok} por Mtok
                  </option>
                );
              })}
            </select>
            <p className="mt-1 text-xs text-ink-400">
              El precio es por millón de tokens (entrada/salida). Cambiar de modelo altera
              cómo conversa el agente, no solo lo que cuesta — pruébalo antes en un tenant
              de prueba.
            </p>
          </Field>

          <Field label="max_tokens (opcional)">
            <input
              type="number"
              value={maxTokens}
              onChange={(e) => setMaxTokens(e.target.value)}
              min={256}
              max={64000}
              placeholder={
                model
                  ? `${MODEL_CATALOG[model as SelectableModelId].defaultMaxTokens} (default del modelo)`
                  : "1024 (default del modelo)"
              }
              className="w-full rounded border border-ink-200 px-3 py-2 text-sm text-ink-800 focus:border-moss focus:outline-none"
            />
            <p className="mt-1 text-xs text-ink-400">
              Techo de tokens por respuesta. Vacío = el default del modelo.
              {showEffort && " En este modelo el razonamiento sale de este mismo techo."}
            </p>
          </Field>

          {showEffort && (
            <Field label="Profundidad de razonamiento (opcional)">
              <select
                value={effort}
                onChange={(e) => setEffort(e.target.value)}
                className="w-full rounded border border-ink-200 px-3 py-2 text-sm text-ink-800 focus:border-moss focus:outline-none"
              >
                <option value="">
                  — Default del modelo ({defaultEffortFor(model) ?? "—"}) —
                </option>
                {DEPLOYMENT_EFFORTS.map((level) => (
                  <option key={level} value={level}>
                    {EFFORT_LABELS[level]}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-ink-400">
                Sube la calidad de razonamiento a cambio de latencia y tokens. Para una
                respuesta de venta de 3–4 oraciones, bajo suele bastar.
              </p>
            </Field>
          )}

          <Field label="Motor de conversación (opcional)">
            <select
              value={engine}
              onChange={(e) => setEngine(e.target.value)}
              className="w-full rounded border border-ink-200 px-3 py-2 text-sm text-ink-800 focus:border-moss focus:outline-none"
            >
              <option value="">— v1 (default) —</option>
              {DEPLOYMENT_ENGINES.map((id) => (
                <option key={id} value={id}>
                  {ENGINE_LABELS[id]}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-ink-400">
              v2 ejecuta primero las acciones y escribe la respuesta una sola vez al
              final, con los resultados a la vista. Cambia cómo se construye el turno —
              pruébalo en un tenant de prueba antes de mover a nadie real.
            </p>
          </Field>

          <div className="pt-2">
            <button
              type="button"
              disabled={!canSave}
              onClick={handleSave}
              className="w-full rounded-md bg-lime px-4 py-2 text-sm font-medium text-moss hover:opacity-90 disabled:opacity-50"
            >
              {createMutation.isPending ? "Creando…" : "Crear deployment"}
            </button>
            <p className="mt-2 text-xs text-ink-400">
              Crear un nuevo deployment archivará automáticamente el actual.
            </p>
          </div>
        </div>
      </div>

      {/* Deployment history */}
      {deployments && deployments.length > 0 && (
        <div>
          <h2 className="mb-3 text-sm font-semibold text-ink-700">Historial</h2>
          <div className="overflow-hidden rounded-lg border border-ink-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-ink-050 text-xs font-semibold uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-4 py-3 text-left">Versión</th>
                  <th className="px-4 py-3 text-left">Agente</th>
                  <th className="px-4 py-3 text-left">Script</th>
                  <th className="px-4 py-3 text-left">Modelo</th>
                  <th className="px-4 py-3 text-left">Estado</th>
                  <th className="px-4 py-3 text-left">Creado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {deployments.map((d) => (
                  <tr key={d.id}>
                    <td className="px-4 py-3 font-mono text-ink-700">v{d.version}</td>
                    <td className="px-4 py-3 text-ink-900">{d.agentName}</td>
                    <td className="px-4 py-3 text-ink-500">
                      {d.script.name} v{d.script.version}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-ink-700">
                        {d.model ? modelLabel(d.model) : "Default del sistema"}
                      </div>
                      {runtimeDetails(d) && (
                        <div className="text-xs text-ink-400">{runtimeDetails(d)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          d.status === "active"
                            ? "bg-green-100 text-green-800"
                            : "bg-ink-100 text-ink-500"
                        }`}
                      >
                        {d.status === "active" ? "Activo" : "Archivado"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ink-500">
                      {new Date(d.createdAt).toLocaleDateString("es-MX")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-ink-400">
            Los valores marcados <span className="font-medium">(default)</span> no los
            guardó esa versión: se resuelven del catálogo de modelos tal como está hoy.
            Para una versión archivada son lo que esa configuración correría ahora, no
            lo que corrió entonces.
          </p>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-ink-600">{label}</label>
      {children}
    </div>
  );
}
